"use client";

import { useState, useMemo } from "react";
import { useTranslations } from "next-intl";
import { Save, AlertTriangle } from "lucide-react";
import { Input } from "@/ui/components/ui/input";
import { Label } from "@/ui/components/ui/label";
import { Button } from "@/ui/components/ui/button";
import { PeptideSelector } from "@/ui/components/calculator/peptide-selector";
import { PresetChips } from "@/ui/components/calculator/preset-chips";
import { accentClassFor } from "@/ui/components/calculator/cockpit";
import { getPeptideBySlug, getPeptideName, PEPTIDE_DATA } from "@/lib/calculator/peptide-data";
import {
	GUEST_PROTOCOLS_MAX,
	loadGuestProtocols,
	saveGuestProtocol,
} from "@/lib/calculator/calculator-storage";
import { getLocaleConfig, type Locale } from "@/config/locale";
import {
	calculateConcentration,
	calculateVolume,
	calculateInsulinUnits,
	recommendSyringe,
	formatDecimalDisplay,
} from "@/lib/calculator/calculations";
import type { TitrationStep, SyringeSize } from "@/lib/calculator/types";

interface TitrationPlannerProps {
	locale: string;
	initialSlug?: string;
	lockedPeptide?: boolean;
}

const TITRATION_SLUGS = PEPTIDE_DATA.filter((p) => p.titrationSchema && p.titrationSchema.length > 0).map(
	(p) => p.slug,
);

// Stockable BAC presets mirror the calculator's BAC chips.
const BAC_PRESETS = [1, 2, 2.5, 3];

function addWeeks(date: Date, weeks: number): Date {
	const result = new Date(date);
	result.setDate(result.getDate() + weeks * 7);
	return result;
}

function formatDate(date: Date, locale: string): string {
	return date.toLocaleDateString(getLocaleConfig(locale).bcp47, {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	});
}

function getTodayString(): string {
	const d = new Date();
	const yyyy = d.getFullYear();
	const mm = String(d.getMonth() + 1).padStart(2, "0");
	const dd = String(d.getDate()).padStart(2, "0");
	return `${yyyy}-${mm}-${dd}`;
}

interface TitrationRow {
	step: TitrationStep;
	startDate: Date;
	endDate: Date;
	volume: number;
	insulinUnits: number;
	/** `null` when the draw exceeds the largest syringe we sell (1 ml) — see recommendSyringe(). */
	syringe: SyringeSize | null;
}

export function TitrationPlanner({ locale, initialSlug, lockedPeptide }: TitrationPlannerProps) {
	const t = useTranslations("peptideCalculator");

	const defaultSlug = initialSlug ?? TITRATION_SLUGS[0] ?? "retatrutide";
	const [peptideSlug, setPeptideSlug] = useState(defaultSlug);

	const currentPeptide = getPeptideBySlug(peptideSlug);
	const [peptideAmount, setPeptideAmount] = useState(currentPeptide?.defaultVialSize ?? 10);
	const [bacWater, setBacWater] = useState(currentPeptide?.defaultBacWater ?? 2);
	const [startDate, setStartDate] = useState(getTodayString());
	const [saved, setSaved] = useState(false);

	const handlePeptideChange = (slug: string) => {
		setPeptideSlug(slug);
		const p = getPeptideBySlug(slug);
		if (p) {
			setPeptideAmount(p.defaultVialSize);
			setBacWater(p.defaultBacWater);
		}
		setSaved(false);
	};

	const hasTitration = !!(currentPeptide?.titrationSchema && currentPeptide.titrationSchema.length > 0);

	const rows = useMemo<TitrationRow[]>(() => {
		if (!hasTitration || !currentPeptide?.titrationSchema) return [];

		const concentration = calculateConcentration(peptideAmount, bacWater);
		const start = new Date(startDate + "T00:00:00");

		return currentPeptide.titrationSchema.map((step) => {
			const weekOffset = step.week - 1;
			const stepStart = addWeeks(start, weekOffset);
			const stepEnd = addWeeks(stepStart, step.durationWeeks);
			const displayEnd = new Date(stepEnd);
			displayEnd.setDate(displayEnd.getDate() - 1);

			const volume = calculateVolume(step.dose, concentration);
			const iu = calculateInsulinUnits(volume);
			const syringe = recommendSyringe(volume);

			return { step, startDate: stepStart, endDate: displayEnd, volume, insulinUnits: iu, syringe };
		});
	}, [hasTitration, currentPeptide, peptideAmount, bacWater, startDate]);

	const peptideName = currentPeptide ? getPeptideName(currentPeptide, locale as Locale) : peptideSlug;
	const accentClass = currentPeptide ? accentClassFor(currentPeptide, locale as Locale) : "text-primary";

	// True when at least one scheduled week draws more than 1 ml — no stock insulin
	// syringe holds that in one draw. Drives the table-level overfillNote.
	const hasOverfillRow = rows.some((row) => row.syringe === null);

	// Per-injection draw-up summary, mono: "{IU} IU · {ml} ml · {syringe} ml Spritze".
	// When no syringe fits (row.syringe === null), the syringe segment is dropped here
	// and rendered as a separate destructive badge (titration.overfillBadge) instead of
	// leaking a literal "null ml" into the UI.
	const drawUpLabel = (row: TitrationRow): string => {
		const iu = row.insulinUnits > 0 ? Math.round(row.insulinUnits) : 0;
		const ml = row.volume > 0 ? formatDecimalDisplay(row.volume, locale, { min: 2, max: 2 }) : "0";
		if (row.syringe === null) return `${iu} IU · ${ml} ml`;
		const syr = formatDecimalDisplay(row.syringe, locale);
		return `${iu} IU · ${ml} ml · ${syr} ml ${t("titration.syringe")}`;
	};

	// Small destructive pill shown next to the draw-up summary when a row overfills.
	// `data-testid` is keyed by week (not surface) since the mobile card stack and the
	// desktop table both render simultaneously in the DOM — CSS (`sm:hidden` /
	// `hidden sm:block`) toggles which is VISIBLE, not which exists — so a test needs a
	// stable per-row hook to scope past that duplication.
	const overfillBadge = (week: number) => (
		<span
			data-testid={`overfill-badge-w${week}`}
			className="bg-destructive/10 inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold text-destructive"
		>
			<AlertTriangle aria-hidden className="h-3 w-3" />
			{t("titration.overfillBadge")}
		</span>
	);

	// Persist the titration plan through the SHARED guest-protocol store so it lands in
	// the canonical `SavedProtocolRecord` shape (kind: "titration") and survives a later
	// single-protocol save. The shared store owns the cap + dedupe; we read it once up
	// front only to warn + skip the prompt when already full.
	const handleSavePlan = () => {
		if (!hasTitration || !currentPeptide) return;
		if (loadGuestProtocols().length >= GUEST_PROTOCOLS_MAX) {
			if (typeof window !== "undefined") window.alert(t("titration.maxReached"));
			return;
		}
		const suggested = `${t("titration.saveDefaultName")} · ${peptideName}`;
		const name =
			typeof window !== "undefined" ? window.prompt(t("titration.saveNamePrompt"), suggested) : suggested;
		if (name == null) return; // user cancelled
		const trimmed = name.trim() || suggested;
		saveGuestProtocol({
			name: trimmed,
			kind: "titration",
			peptideSlug,
			peptideAmount,
			bacWater,
			startDate,
		});
		setSaved(true);
	};

	return (
		<div className="space-y-4 sm:space-y-6">
			{/* Intro */}
			<p className="text-xs leading-relaxed text-muted-foreground sm:text-sm">{t("titration.intro")}</p>

			{/* Inputs */}
			<div className="grid gap-4 sm:gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
				{/* Peptide selector — full width on its row */}
				<div className="space-y-1.5 lg:col-span-2">
					<Label className="text-xs font-semibold sm:text-sm">{t("form.peptide")}</Label>
					<PeptideSelector
						selectedSlug={peptideSlug}
						onChange={handlePeptideChange}
						locale={locale}
						disabled={lockedPeptide}
					/>
				</div>

				{/* Vial size — chip row */}
				<div className="space-y-2">
					<Label className="text-xs font-semibold sm:text-sm">{t("form.peptideAmount")}</Label>
					<PresetChips
						values={currentPeptide?.defaultVialSizes ?? [peptideAmount]}
						selected={peptideAmount}
						onChange={(v) => {
							setPeptideAmount(v);
							setSaved(false);
						}}
						unit="mg"
						accessibleLabel={t("form.peptideAmount")}
						min={0}
					/>
				</div>

				{/* BAC water — chip row */}
				<div className="space-y-2">
					<Label className="text-xs font-semibold sm:text-sm">{t("form.bacWater")}</Label>
					<PresetChips
						values={BAC_PRESETS}
						selected={bacWater}
						onChange={(v) => {
							setBacWater(v);
							setSaved(false);
						}}
						unit="ml"
						accessibleLabel={t("form.bacWater")}
						min={0.1}
					/>
				</div>

				{/* Start date */}
				<div className="space-y-2 lg:col-span-2">
					<Label className="text-xs font-semibold sm:text-sm">{t("form.startDate")}</Label>
					<Input
						type="date"
						value={startDate}
						onChange={(e) => {
							setStartDate(e.target.value);
							setSaved(false);
						}}
						className="min-h-11 max-w-[12rem] font-mono tabular-nums"
					/>
				</div>
			</div>

			{/* No titration schema — honest message, never auto-generated */}
			{!hasTitration && (
				<div className="rounded-[14px] border border-border bg-secondary px-3 py-3 text-xs text-muted-foreground sm:px-4 sm:text-sm">
					{t("titration.noSchema", { peptide: peptideName })}
				</div>
			)}

			{/* Titration plan */}
			{hasTitration && rows.length > 0 && (
				<>
					{/* Mobile: card stack */}
					<div className="space-y-2 sm:hidden">
						{rows.map((row) => {
							const weekEnd = row.step.week + row.step.durationWeeks - 1;
							return (
								<div
									key={row.step.week}
									className="rounded-[14px] border border-border bg-card p-3.5 shadow-sm"
								>
									<div className="flex items-baseline justify-between gap-3">
										<span className="text-[11px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
											{t("titration.week")}{" "}
											<span className="font-mono text-sm tabular-nums text-foreground">
												{row.step.week}
												{"–"}
												{weekEnd}
											</span>
										</span>
										<span className={`font-mono text-lg font-bold tabular-nums ${accentClass}`}>
											{formatDecimalDisplay(row.step.dose, locale)} mg
										</span>
									</div>
									<div className="mt-1.5 text-xs text-muted-foreground">
										{formatDate(row.startDate, locale)} {"–"} {formatDate(row.endDate, locale)}
									</div>
									<div className="text-foreground/80 mt-2 flex flex-wrap items-center gap-1.5 border-t border-border pt-2 font-mono text-xs tabular-nums">
										<span>{drawUpLabel(row)}</span>
										{row.syringe === null && overfillBadge(row.step.week)}
									</div>
								</div>
							);
						})}
					</div>

					{/* Desktop: table */}
					<div className="hidden overflow-x-auto rounded-[14px] border border-border shadow-sm sm:block">
						<table className="w-full text-sm">
							<thead>
								<tr className="border-b border-border bg-secondary text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
									<th className="px-4 py-3">{t("titration.week")}</th>
									<th className="px-4 py-3">{t("titration.dose")}</th>
									<th className="px-4 py-3">{t("titration.dates")}</th>
									<th className="px-4 py-3 text-right">{t("titration.syringe")}</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((row, idx) => {
									const weekEnd = row.step.week + row.step.durationWeeks - 1;
									return (
										<tr
											key={row.step.week}
											className={`border-b border-border last:border-0 ${
												idx % 2 === 0 ? "bg-card" : "bg-secondary/30"
											}`}
										>
											<td className="px-4 py-3 font-medium text-foreground">
												{t("titration.week")}{" "}
												<span className="font-mono tabular-nums">
													{row.step.week}
													{"–"}
													{weekEnd}
												</span>
											</td>
											<td className={`px-4 py-3 font-mono font-bold tabular-nums ${accentClass}`}>
												{formatDecimalDisplay(row.step.dose, locale)} mg
											</td>
											<td className="px-4 py-3 text-muted-foreground">
												{formatDate(row.startDate, locale)} {"–"} {formatDate(row.endDate, locale)}
											</td>
											<td className="text-foreground/80 px-4 py-3 text-right font-mono tabular-nums">
												<div className="flex items-center justify-end gap-1.5">
													<span>{drawUpLabel(row)}</span>
													{row.syringe === null && overfillBadge(row.step.week)}
												</div>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>

					{/* Table-level overfill note — only when at least one scheduled week
					    exceeds 1 ml. Offers the honest remedies (less BAC water, or split
					    the dose); never suggests a bigger syringe (none exists). */}
					{hasOverfillRow && (
						<div
							data-testid="titration-overfill-note"
							className="border-destructive/30 bg-destructive/10 rounded-xl border p-3"
						>
							<div className="flex items-start gap-2 text-xs leading-relaxed text-foreground sm:text-sm">
								<AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
								<span>{t("titration.overfillNote")}</span>
							</div>
						</div>
					)}

					{/* Footer: disclaimer + save */}
					<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
						<p className="text-[11px] leading-relaxed text-muted-foreground sm:max-w-md">
							{t("titration.disclaimer")}
						</p>
						<Button
							type="button"
							variant="outline-solid"
							size="sm"
							onClick={handleSavePlan}
							className="min-h-11 shrink-0 gap-2"
						>
							<Save aria-hidden className="h-4 w-4" />
							{saved ? t("titration.savePlanSaved") : t("titration.savePlan")}
						</Button>
					</div>
				</>
			)}
		</div>
	);
}
